import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { searchApi } from '@/services/searchApi';
import { useNavigate } from 'react-router-dom';

interface SearchSuggestion {
  text: string;
  type: string;
}

interface GlobalSearchBarProps {
  className?: string;
  placeholder?: string;
  onResultClick?: (result: any) => void;
}

export function GlobalSearchBar({ 
  className = '',
  placeholder = 'Search CXOs, briefs, articles...',
  onResultClick
}: GlobalSearchBarProps) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const fetchSuggestions = useCallback(async (value: string) => {
    if (value.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await searchApi.getSuggestions(value, 8);
      if (response.success) {
        setSuggestions(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchSuggestions(query);
    }, 200);

    return () => clearTimeout(timeout);
  }, [query, fetchSuggestions]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setIsOpen(false);
      setQuery('');
    }
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    setQuery(suggestion.text);
    navigate(`/search?q=${encodeURIComponent(suggestion.text)}`);
    setIsOpen(false);
    setQuery('');
  };

  const getSuggestionIcon = (type: string) => {
    switch (type) {
      case 'cxo':
        return '👤';
      case 'industry':
        return '🏭';
      case 'expertise':
        return '💡';
      case 'tag':
        return '🏷️';
      default:
        return '🔍';
    }
  };

  return (
    <div className={`relative ${className}`}>
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            className="w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setSuggestions([]);
                inputRef.current?.focus();
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Suggestions Dropdown */}
        {isOpen && (query.length >= 2 || suggestions.length > 0) && (
          <>
            <div 
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border z-50 max-h-80 overflow-y-auto">
              {isLoading && suggestions.length === 0 && (
                <div className="p-4 text-center text-gray-500">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                </div>
              )}
              
              {suggestions.length > 0 && (
                <div className="py-2">
                  {suggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3"
                    >
                      <span>{getSuggestionIcon(suggestion.type)}</span>
                      <div className="flex-1">
                        <span className="font-medium">{suggestion.text}</span>
                        <span className="text-xs text-gray-400 ml-2 capitalize">
                          {suggestion.type}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {query.length >= 2 && !isLoading && (
                <button
                  type="submit"
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 border-t flex items-center gap-2 text-blue-600"
                >
                  <Search className="w-4 h-4" />
                  <span>Search for "{query}"...</span>
                </button>
              )}
            </div>
          </>
        )}
      </form>
    </div>
  );
}

export default GlobalSearchBar;
